import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {SpecificationCharacteristic} from '../entity/specification-characteristic';
import {GuruEntity} from '../model/guru-entity';
import {GoogleService} from './google-service';
import {GuruCharacteristic} from '../model/guru-characteristic';
import {ConfigurationSaveInformation} from '../model/configuration-save-information';
import {EntityLineHeader} from '../entity/entity-line-header';
import {MassUpdate} from '../model/mass-update';
import {ConfigurationData} from '../model/configuration-data';
import {LineResequenceInput} from '../model/line-resequence-input';
import {UnitRatingResponse} from '../model/unit-rating-response';
import {EntityLine} from '../entity/entity-line';
import {PersonLoginService} from './person-login-service';

@Service()
export class ModelService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);
  private readonly personLoginService = inject(PersonLoginService);


  private urlBase = environment.serviceUrl;

  getEngineKey(propertyName: string): Observable<string> {
    let url = this.urlBase + 'model/engineKey/' + propertyName;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  loadConfiguration(engineKey: string, characteristics: SpecificationCharacteristic[]): Observable<string> {
    let url = this.urlBase + 'model/loadConfiguration/' + engineKey;
    let body = JSON.stringify(characteristics);
    return this.http.post(url, body, {headers: this.getPostHeaders('application/json'), responseType: 'text'});
  }

  private getPostHeaders(contentType: string): HttpHeaders {
    return this.getRequestHeaders().set('Content-Type', contentType);
  }

  setValue(entityName: string, engineKey: string, value: string): Observable<string> {
    let url = this.urlBase + 'model/setValue/' + entityName + '/' + engineKey;
    return this.http.post(url, value, {headers: this.getPostHeaders('text/plain'), responseType: 'text'});
  }

  getInvalidEntities(engineKey: string): Observable<string[]> {
    let url = this.urlBase + 'model/invalidEntities/' + engineKey;
    return this.http.get<string[]>(url, {headers: this.getRequestHeaders()});
  }

  isComplete(engineKey: string): Observable<boolean> {
    let url = this.urlBase + 'model/isComplete/' + engineKey;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'}).pipe(map(response => {
      return 'true' === response;
    }));
  }

  isValid(engineKey: string): Observable<boolean> {
    let url = this.urlBase + 'model/isValid/' + engineKey;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'}).pipe(map(response => {
      return 'true' === response;
    }));
  }

  executeHierarchy(engineKey: string, hierarchyName: string): Observable<GuruEntity[]> {
    let url = this.urlBase + 'model/executeHierarchy/' + engineKey;
    return this.http.post<GuruEntity[]>(url, hierarchyName, {headers: this.getPostHeaders('text/plain')});
  }

  getUserSelections(engineKey: string): Observable<GuruEntity[]> {
    let url = this.urlBase + 'model/userSelections/' + engineKey;
    return this.http.get<GuruEntity[]>(url, {headers: this.getRequestHeaders()});
  }

  getCharacteristics(engineKey: string): Observable<SpecificationCharacteristic[]> {
    let url = this.urlBase + 'model/characteristics/' + engineKey;
    return this.http.get<SpecificationCharacteristic[]>(url, {headers: this.getRequestHeaders()});
  }

  getEntity(engineKey: string, entityName: string): Observable<GuruEntity> {
    const url = this.urlBase + 'model/entity/' + engineKey + '/' + entityName;
    return this.http.get<GuruEntity>(url, {headers: this.getRequestHeaders()});
  }

  getEntitiesStartingWithName(engineKey: string, entityName: string): Observable<GuruEntity[]> {
    const url = this.urlBase + 'model/entitiesStartingWithName/' + engineKey + '/' + entityName;
    return this.http.get<GuruEntity[]>(url, {headers: this.getRequestHeaders()});
  }

  getCharacteristicValue(engineKey: string, entityName: string, characteristicName: string): Observable<string> {
    const url = this.urlBase + 'model/characteristicValue/' + engineKey + '/' + entityName + '/' + characteristicName;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  getEntityCharacteristicValue(engineKey: string, entityName: string, characteristicName: string, entityValue: string): Observable<string> {
    const url = this.urlBase + 'model/entityCharacteristicValue/' + engineKey + '/' + encodeURIComponent(entityName) + '/' + encodeURIComponent(characteristicName) + '/' + encodeURIComponent(entityValue);
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  getGuruCharacteristic(engineKey: string, entityName: string, characteristicName: string): Observable<GuruCharacteristic> {
    const subject = new Subject<GuruCharacteristic>();
    this.getCharacteristicValue(engineKey, entityName, characteristicName).subscribe(value => {
      const characteristic = new GuruCharacteristic();
      characteristic.entityName = entityName;
      characteristic.propertyName = characteristicName;
      characteristic.value = value;
      subject.next(characteristic);
    });
    return subject.asObservable();
  }

  getMessages(engineKey: string): Observable<string[]> {
    const url = this.urlBase + 'model/messages/' + engineKey;
    return this.http.get<string[]>(url, {headers: this.getRequestHeaders()});
  }

  getConfigurationData(engineKey: string, priceBasisDate: any, priceListId: string): Observable<ConfigurationData> {
    let brandID = this.personLoginService.getBrandIDs();
    const url = this.urlBase + 'model/configurationData/' + engineKey + '/' + priceBasisDate + '/' + priceListId + '/' + brandID;
    return this.http.get<ConfigurationData>(url, {headers: this.getRequestHeaders()});
  }

  saveConfiguration(information: ConfigurationSaveInformation): Observable<string> {
    const url = this.urlBase + 'model/saveConfiguration';
    const body = JSON.stringify(information);
    return this.http.post(url, body, {headers: this.getPostHeaders('application/json'), responseType: 'text'});
  }

  saveConfigurations(configurations: ConfigurationSaveInformation[]): Observable<EntityLine[]> {
    const url = this.urlBase + 'model/saveConfigurations';
    const body = JSON.stringify(configurations);
    return this.http.post<EntityLine[]>(url, body, {headers: this.getPostHeaders('application/json')});
  }

  addConfiguration(productId: string, information: ConfigurationSaveInformation): Observable<EntityLineHeader> {
    const url = this.urlBase + 'model/addConfiguration/' + productId;
    const body = JSON.stringify(information);
    return this.http.post<EntityLineHeader>(url, body, {headers: this.getPostHeaders('application/json')});
  }

  copyConfiguration(information: ConfigurationSaveInformation): Observable<EntityLineHeader> {
    const url = this.urlBase + 'model/copyConfiguration';
    const body = JSON.stringify(information);
    return this.http.post<EntityLineHeader>(url, body, {headers: this.getPostHeaders('application/json')});
  }

  applyMassUpdate(massUpdate: MassUpdate): Observable<string> {
    const url = this.urlBase + 'model/saveConfiguration';
    const body = JSON.stringify(massUpdate);
    return this.http.post(url, body, {headers: this.getPostHeaders('application/json'), responseType: 'text'});
  }

  resequenceLines(input: LineResequenceInput): Observable<string> {
    const url = this.urlBase + 'model/resequenceLines';
    const body = JSON.stringify(input);
    return this.http.post(url, body, {headers: this.getPostHeaders('application/json'), responseType: 'text'});
  }

  setEngineRatingValues(engineKey: string, productId: string, ratingResponse: UnitRatingResponse): Observable<string> {
    const url = this.urlBase + 'model/setEngineRatingValues/' + engineKey + '/' + productId;
    const body = JSON.stringify(ratingResponse);
    return this.http.post(url, body, {headers: this.getPostHeaders('application/json'), responseType: 'text'});
  }

  removeEngine(engineKey: string): Observable<string> {
    const url = this.urlBase + 'model/removeEngine';
    return this.http.post(url, engineKey, {headers: this.getPostHeaders('text/plain'), responseType: 'text'});
  }

}
